@if (missingItems().length > 0) {
  @if (variant() === 'card') {
    <!-- ========================================== -->
    <!-- VARIANT CARD (Fiche complète / Property View) -->
    <!-- ========================================== -->
    <div
      class="rounded-3xl border border-amber-a5/80 bg-amber-a1/60 dark:bg-amber-1/30 backdrop-blur-xl p-5 sm:p-6 shadow-sm flex flex-col gap-4 transition-all"
    >
      <!-- Header: Title, Completion counter & Filter tabs + Toggle button -->
      <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div class="flex items-center gap-3.5">
          <div
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-a3 text-amber-11 border border-amber-a6 shadow-2xs"
          >
            <mat-icon class="text-xl w-5 h-5">checklist</mat-icon>
          </div>
          <div>
            <div class="flex items-center gap-2.5 flex-wrap">
              <h3 class="text-base font-bold text-gray-12 m-0">Informations recommandées & manquantes</h3>
              <span
                class="px-2.5 py-0.5 rounded-full bg-amber-a3 text-amber-11 border border-amber-a5 text-xs font-bold"
              >
                {{ completedChecks() }}/{{ totalEvaluatedChecks }} complétés ({{ completionPercentage() }}%)
              </span>
            </div>
            <p class="text-xs text-gray-11 m-0 mt-1">
              Complétez ces informations pour maximiser l'attractivité de votre annonce et accélérer les
              offres d'achat.
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2 flex-wrap self-start lg:self-auto">
          <!-- Filter tabs (visible when expanded) -->
          @if (expanded()) {
            <div class="flex items-center gap-1.5 flex-wrap">
              <button
                type="button"
                (click)="setFilter('all')"
                class="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                [class.bg-gray-12]="activeFilter() === 'all'"
                [class.text-gray-1]="activeFilter() === 'all'"
                [class.bg-gray-a3]="activeFilter() !== 'all'"
                [class.text-gray-11]="activeFilter() !== 'all'"
                [class.hover:bg-gray-a4]="activeFilter() !== 'all'"
              >
                Tous ({{ missingItems().length }})
              </button>
              @if (criticalCount() > 0) {
                <button
                  type="button"
                  (click)="setFilter('critical')"
                  class="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                  [class.bg-ruby-9]="activeFilter() === 'critical'"
                  [class.text-white]="activeFilter() === 'critical'"
                  [class.bg-ruby-a3]="activeFilter() !== 'critical'"
                  [class.text-ruby-11]="activeFilter() !== 'critical'"
                  [class.hover:bg-ruby-a4]="activeFilter() !== 'critical'"
                >
                  Bloquants ({{ criticalCount() }})
                </button>
              }
              @if (warningCount() > 0) {
                <button
                  type="button"
                  (click)="setFilter('warning')"
                  class="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                  [class.bg-amber-9]="activeFilter() === 'warning'"
                  [class.text-amber-1]="activeFilter() === 'warning'"
                  [class.bg-amber-a3]="activeFilter() !== 'warning'"
                  [class.text-amber-11]="activeFilter() !== 'warning'"
                  [class.hover:bg-amber-a4]="activeFilter() !== 'warning'"
                >
                  Recommandés ({{ warningCount() }})
                </button>
              }
              @if (infoCount() > 0) {
                <button
                  type="button"
                  (click)="setFilter('info')"
                  class="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                  [class.bg-indigo-9]="activeFilter() === 'info'"
                  [class.text-white]="activeFilter() === 'info'"
                  [class.bg-indigo-a3]="activeFilter() !== 'info'"
                  [class.text-indigo-11]="activeFilter() !== 'info'"
                  [class.hover:bg-indigo-a4]="activeFilter() !== 'info'"
                >
                  Conseillés ({{ infoCount() }})
                </button>
              }
            </div>
          }

          <!-- Toggle Button -->
          <button
            type="button"
            (click)="toggleExpanded()"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-amber-a6 bg-amber-a3 hover:bg-amber-a4 text-amber-12 text-xs font-semibold transition-colors cursor-pointer shadow-2xs"
          >
            <span>{{ expanded() ? 'Réduire' : 'Afficher les détails' }}</span>
            <span class="px-1.5 py-0.5 rounded-full bg-amber-a5 text-amber-12 text-[10px] font-bold">
              {{ missingItems().length }}
            </span>
            <mat-icon
              class="text-xs w-3.5 h-3.5 transition-transform duration-200"
              [class.rotate-180]="expanded()"
            >
              expand_more
            </mat-icon>
          </button>
        </div>
      </div>

      <!-- Collapsible Grid (closed by default) -->
      <div
        class="grid transition-all duration-300 ease-out"
        [class.grid-rows-[1fr]]="expanded()"
        [class.grid-rows-[0fr]]="!expanded()"
      >
        <div class="overflow-hidden">
          <div
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 pt-4 border-t border-amber-a4/60"
          >
            @for (item of filteredMissingItems(); track item.id) {
              <ng-container
                [ngTemplateOutlet]="itemCardTpl"
                [ngTemplateOutletContext]="{ $implicit: item }"
              />
            }
          </div>
        </div>
      </div>
    </div>
  } @else {
    <!-- ========================================== -->
    <!-- VARIANT DRAWER (Header d'édition / Status Header) -->
    <!-- ========================================== -->
    <div
      class="grid transition-all duration-300 ease-out border-t border-transparent"
      [class.grid-rows-[1fr]]="expanded()"
      [class.grid-rows-[0fr]]="!expanded()"
      [class.border-gray-5/50]="expanded()"
    >
      <div class="overflow-hidden">
        <div
          class="bg-white/85 dark:bg-gray-1/85 backdrop-blur-2xl p-4 sm:p-5 transition-all duration-300 transform"
          [class.opacity-100]="expanded()"
          [class.translate-y-0]="expanded()"
          [class.opacity-0]="!expanded()"
          [class.-translate-y-2]="!expanded()"
        >
          <!-- Drawer Header: Title & Filter tabs -->
          <div
            class="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-gray-5"
          >
            <div class="flex items-center gap-3">
              <div
                class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-a3 text-amber-11"
              >
                <mat-icon class="text-base w-4 h-4">checklist</mat-icon>
              </div>
              <div>
                <div class="flex items-center gap-2">
                  <h3 class="text-sm font-bold text-gray-12 m-0">Informations recommandées & manquantes</h3>
                  <span class="px-2 py-0.5 rounded-full bg-gray-a3 text-gray-11 text-[11px] font-bold">
                    {{ completedChecks() }}/{{ totalEvaluatedChecks }} complétés
                  </span>
                </div>
                <p class="text-xs text-gray-11 m-0 mt-0.5">
                  Complétez ces informations pour maximiser l'attractivité de votre annonce et accélérer les
                  offres.
                </p>
              </div>
            </div>

            <!-- Filter tabs -->
            <div class="flex items-center gap-1.5 flex-wrap self-start md:self-auto">
              <button
                type="button"
                (click)="setFilter('all')"
                class="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                [class.bg-gray-12]="activeFilter() === 'all'"
                [class.text-gray-1]="activeFilter() === 'all'"
                [class.bg-gray-a3]="activeFilter() !== 'all'"
                [class.text-gray-11]="activeFilter() !== 'all'"
                [class.hover:bg-gray-a4]="activeFilter() !== 'all'"
              >
                Tous ({{ missingItems().length }})
              </button>
              @if (criticalCount() > 0) {
                <button
                  type="button"
                  (click)="setFilter('critical')"
                  class="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                  [class.bg-ruby-9]="activeFilter() === 'critical'"
                  [class.text-white]="activeFilter() === 'critical'"
                  [class.bg-ruby-a3]="activeFilter() !== 'critical'"
                  [class.text-ruby-11]="activeFilter() !== 'critical'"
                  [class.hover:bg-ruby-a4]="activeFilter() !== 'critical'"
                >
                  Bloquants ({{ criticalCount() }})
                </button>
              }
              @if (warningCount() > 0) {
                <button
                  type="button"
                  (click)="setFilter('warning')"
                  class="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                  [class.bg-amber-9]="activeFilter() === 'warning'"
                  [class.text-amber-1]="activeFilter() === 'warning'"
                  [class.bg-amber-a3]="activeFilter() !== 'warning'"
                  [class.text-amber-11]="activeFilter() !== 'warning'"
                  [class.hover:bg-amber-a4]="activeFilter() !== 'warning'"
                >
                  Recommandés ({{ warningCount() }})
                </button>
              }
              @if (infoCount() > 0) {
                <button
                  type="button"
                  (click)="setFilter('info')"
                  class="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                  [class.bg-indigo-9]="activeFilter() === 'info'"
                  [class.text-white]="activeFilter() === 'info'"
                  [class.bg-indigo-a3]="activeFilter() !== 'info'"
                  [class.text-indigo-11]="activeFilter() !== 'info'"
                  [class.hover:bg-indigo-a4]="activeFilter() !== 'info'"
                >
                  Conseillés ({{ infoCount() }})
                </button>
              }
            </div>
          </div>

          <!-- Items Grid -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mt-4">
            @for (item of filteredMissingItems(); track item.id) {
              <ng-container
                [ngTemplateOutlet]="itemCardTpl"
                [ngTemplateOutletContext]="{ $implicit: item }"
              />
            }
          </div>
        </div>
      </div>
    </div>
  }

  <!-- ========================================== -->
  <!-- ITEM CARD TEMPLATE (Shared by both variants) -->
  <!-- ========================================== -->
  <ng-template #itemCardTpl let-item>
    <div
      class="rounded-xl p-4 border transition-all flex flex-col justify-between gap-3 shadow-xs"
      [class.bg-ruby-1]="item.severity === 'critical'"
      [class.border-ruby-a6]="item.severity === 'critical'"
      [class.hover:border-ruby-8]="item.severity === 'critical'"
      [class.bg-amber-1]="item.severity === 'warning'"
      [class.border-amber-a6]="item.severity === 'warning'"
      [class.hover:border-amber-8]="item.severity === 'warning'"
      [class.bg-indigo-1]="item.severity === 'info'"
      [class.border-indigo-a6]="item.severity === 'info'"
      [class.hover:border-indigo-8]="item.severity === 'info'"
    >
      <div>
        <div class="flex items-center justify-between gap-2 mb-2">
          <span
            class="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
            [class.bg-ruby-a3]="item.severity === 'critical'"
            [class.text-ruby-11]="item.severity === 'critical'"
            [class.bg-amber-a3]="item.severity === 'warning'"
            [class.text-amber-11]="item.severity === 'warning'"
            [class.bg-indigo-a3]="item.severity === 'info'"
            [class.text-indigo-11]="item.severity === 'info'"
          >
            {{ item.section }}
          </span>

          <span
            class="text-[10px] font-semibold"
            [class.text-ruby-10]="item.severity === 'critical'"
            [class.text-amber-10]="item.severity === 'warning'"
            [class.text-indigo-10]="item.severity === 'info'"
          >
            @if (item.severity === 'critical') {
              Bloquant
            } @else if (item.severity === 'warning') {
              Recommandé
            } @else {
              Conseillé
            }
          </span>
        </div>

        <div class="flex items-start gap-2.5">
          <mat-icon
            class="text-base w-4 h-4 shrink-0 mt-0.5"
            [class.text-ruby-9]="item.severity === 'critical'"
            [class.text-amber-9]="item.severity === 'warning'"
            [class.text-indigo-9]="item.severity === 'info'"
          >
            {{ item.icon }}
          </mat-icon>
          <div>
            <h4 class="text-xs font-bold text-gray-12 m-0 leading-snug">{{ item.title }}</h4>
            <p class="text-[11px] text-gray-11 m-0 mt-1 leading-relaxed">{{ item.description }}</p>
          </div>
        </div>
      </div>

      <div class="pt-2 border-t border-gray-5/60 mt-1">
        @if (mode() === 'edit') {
          <button
            mat-stroked-button
            type="button"
            (click)="handleAction(item)"
            class="w-full text-xs! font-semibold! py-1! px-3! h-auto! cursor-pointer flex justify-between items-center"
            [class.border-ruby-7!]="item.severity === 'critical'"
            [class.text-ruby-11!]="item.severity === 'critical'"
            [class.hover:bg-ruby-3!]="item.severity === 'critical'"
            [class.border-amber-7!]="item.severity === 'warning'"
            [class.text-amber-11!]="item.severity === 'warning'"
            [class.hover:bg-amber-3!]="item.severity === 'warning'"
            [class.border-indigo-7!]="item.severity === 'info'"
            [class.text-indigo-11!]="item.severity === 'info'"
            [class.hover:bg-indigo-3!]="item.severity === 'info'"
          >
            <span>{{ item.actionLabel }}</span>
            <mat-icon class="text-xs! w-3.5! h-3.5! ml-1.5 -mr-0.5">arrow_forward</mat-icon>
          </button>
        } @else {
          <a
            mat-stroked-button
            [routerLink]="['/properties', property()._id, 'edit']"
            [fragment]="item.anchorId"
            class="w-full text-xs! font-semibold! py-1! px-3! h-auto! cursor-pointer flex justify-between items-center"
            [class.border-ruby-7!]="item.severity === 'critical'"
            [class.text-ruby-11!]="item.severity === 'critical'"
            [class.hover:bg-ruby-3!]="item.severity === 'critical'"
            [class.border-amber-7!]="item.severity === 'warning'"
            [class.text-amber-11!]="item.severity === 'warning'"
            [class.hover:bg-amber-3!]="item.severity === 'warning'"
            [class.border-indigo-7!]="item.severity === 'info'"
            [class.text-indigo-11!]="item.severity === 'info'"
            [class.hover:bg-indigo-3!]="item.severity === 'info'"
          >
            <span>{{ item.actionLabel }}</span>
            <mat-icon class="text-xs! w-3.5! h-3.5! ml-1.5 -mr-0.5">arrow_forward</mat-icon>
          </a>
        }
      </div>
    </div>
  </ng-template>
}
